// src/pages/settings/create-organization/CreateOrganizationExtensionsPage.tsx
// [UI 2026-10-11] Extensions: onboarding glass cards. Prices sit beside the counters.
import React, { useEffect, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, MessageCircle, Phone, Video } from 'lucide-react';
import { useTranslation } from '../../../contexts/languages/LanguageContext';
import OnboardingFrame from '../../misc/onboarding/OnboardingFrame';
import { publicJourney } from '../../misc/onboarding/continuousPath';
import type { ExtensionId } from '../../misc/onboarding/priceBook';
import { BUILDER_CEILINGS, isStructureDraftReady, loadStructureDraft, structureCeilings } from '../../misc/onboarding/structureDraft';
import { PRICES_FAILED_LINE, usePriceBook } from '../../misc/onboarding/usePriceBook';
import OnboardingTreeView from './OnboardingTreeView';
import '../../misc/onboarding/builder.css';
import './orgFlow.css';
import {
  loadOrgSetup,
  saveOrgSetup,
  type OrgAddons,
  type VideoScope,
} from './orgSetup';
import { ORG_EXTENSION_MAX, cycleTotalLabelKey, extensionUnit, formatMxn, orderQuote } from './orderQuote';

function Qty({
  value,
  max,
  onChange,
}: {
  value: number;
  max: number;
  onChange: (n: number) => void;
}) {
  const stop = (event: React.MouseEvent) => event.stopPropagation();
  return (
    <span className="org-qty" onClick={stop} onMouseDown={stop}>
      <button type="button" disabled={value <= 0} onClick={() => onChange(value - 1)} aria-label="-">−</button>
      <span>{value}</span>
      <button type="button" disabled={value >= max} onClick={() => onChange(value + 1)} aria-label="+">+</button>
    </span>
  );
}

// [A11Y] One paid extension row (QA-F07, Q-1 (a)): the row's round mark is its checkbox - its role, its state, its place in the tab
// order and Space / Enter - named by the row's title. The row keeps its click for the pointer; the counter and the price sit beside the
// checkbox, never inside it (a checkbox's children are presentational, so a button inside one would be hidden from a screen reader).
// A locked row's checkbox is aria-disabled and out of the tab order; the row's own handler refuses the change, by click or by key alike.
function ToggleRow({
  on,
  look = 'check',
  locked = false,
  onToggle,
  label,
  children,
}: {
  on: boolean;
  look?: 'check' | 'radio';
  locked?: boolean;
  onToggle: () => void;
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  const labelId = useId();
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    onToggle();
  };
  return (
    <div className={`org-row${locked ? ' is-locked' : ''}`} onClick={onToggle}>
      <span
        className={`org-${look}${on ? ' on' : ''}`}
        role="checkbox"
        aria-checked={on}
        aria-disabled={locked || undefined}
        aria-labelledby={labelId}
        tabIndex={locked ? -1 : 0}
        onKeyDown={onKeyDown}
      >
        {look === 'check' && on ? <Check size={14} strokeWidth={3} /> : null}
      </span>
      <span id={labelId} className="org-row-label">{label}</span>
      {children}
    </div>
  );
}

const CreateOrganizationExtensionsPage: React.FC = () => {
  // [HANDOFF] One flow: /onboarding/extensiones (Continue goes to /onboarding/cuenta). The logged-in version has no route on this branch.
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  // [PRICING] The price book is read once per mount (S-9): every price beside a row and the floating total come from it, never typed here.
  const { book, failed, retry } = usePriceBook();
  // The draft is read once per book state, inside the book's ceilings (S-16), so the sellers this page caps by are the book's.
  const ceilings = useMemo(() => (book === null ? null : structureCeilings(book)) ?? BUILDER_CEILINGS, [book]);
  const draft = useMemo(() => loadStructureDraft(ceilings), [ceilings]);
  // [PRICING] sellers / teams / companies are the MAXIMUM quantity for each extension ("companies" = the organization, always 1). They are NOT the billed quantity.
  // The billed quantity is whatever the person sets with the +/- counter (addons.*Qty). Stripe must use the chosen qty.
  const sellers = Math.max(1, draft.asesores || 1);
  const teams = Math.max(1, draft.equipos || 1);
  const companies = ORG_EXTENSION_MAX;
  // [PRICING] The saved plan as the plan page left it: a plan the structure outgrows was swapped there (S-2).
  const planId = loadOrgSetup().planId;
  const lightPlan = planId === 'free';
  const videoCap = (scope: VideoScope) => (scope === 'team' ? teams : scope === 'company' ? companies : sellers);
  // [PRICING] [HANDOFF] Every quantity this screen holds is inside its cap, so the quote bills what the screen shows (SCREEN EQUALS CHARGE).
  // Rules enforced: phone off -> voice and voice Pro off; basic voice and Voice Pro are mutually exclusive;
  // Voice Pro is not allowed on Light; voice quantities can never exceed the phone quantity; phone quantity never exceeds advisors;
  // the WhatsApp API and Zoom quantities never exceed the sellers, the organization or the scope's maximum (orderQuote.ts caps them the same way).
  const clean = (next: OrgAddons): OrgAddons => {
    const phoneOn = Boolean(next.phone && (next.phoneQty || 0) > 0);
    const phoneQty = phoneOn ? Math.min(sellers, next.phoneQty) : 0;
    const voice = phoneOn && next.voice && !(next.voice && next.voicePro);
    const voicePro = phoneOn && !lightPlan && next.voicePro && !voice;
    return {
      ...next,
      qtyChosen: true,
      phone: phoneOn,
      phoneQty,
      voice,
      voiceQty: voice ? Math.min(phoneQty, Math.max(1, next.voiceQty || phoneQty)) : 0,
      voicePro,
      voiceProQty: voicePro ? Math.min(phoneQty, Math.max(1, next.voiceProQty || phoneQty)) : 0,
      waSellerQty: Math.min(sellers, next.waSellerQty),
      waCompanyQty: Math.min(companies, next.waCompanyQty),
      videoQty: Math.min(videoCap(next.videoScope), next.videoQty),
    };
  };
  // [PRICING] First visit only (qtyChosen not set yet): every extension that is already on gets its quantity set to its maximum.
  // After that the person's own quantities are kept, even if they chose 1 - cleaned at every read, so a structure whose sellers fell
  // after the extensions were chosen never shows or bills a stale quantity; what the screen shows is saved (idempotent).
  const [addons, setAddons] = useState<OrgAddons>(() => {
    const setup = loadOrgSetup();
    const saved = setup.addons;
    const filled: OrgAddons = {
      ...saved,
      qtyChosen: true,
      phoneQty: saved.phone ? sellers : saved.phoneQty,
      voiceQty: saved.voice ? sellers : saved.voiceQty,
      voiceProQty: saved.voicePro ? sellers : saved.voiceProQty,
      waSellerQty: saved.waSeller ? sellers : saved.waSellerQty,
      waTeamQty: saved.waTeam ? teams : saved.waTeamQty,
      waCompanyQty: saved.waCompany ? companies : (saved.waCompanyQty || 1),
      videoQty: saved.videoIncluded && saved.videoScope === 'person' ? sellers : saved.videoQty,
    };
    const next = clean(saved.qtyChosen ? saved : filled);
    saveOrgSetup({ ...setup, addons: next });
    return next;
  });

  // [PRICING] [HANDOFF] Every change goes through here. It cleans the choice (the rules above), then saves it to localStorage (ventaje_org_setup_v1).
  const persist = (next: OrgAddons) => {
    const fixed = clean(next);
    setAddons(fixed);
    saveOrgSetup({ ...loadOrgSetup(), addons: fixed });
  };

  // [PRICING] The floating price at the bottom = extensions only, for the chosen cycle: monthly = quote.extras "al mes";
  // 6 months / annual = quote.extrasDue (extras x months) "por 6 / 12 meses". The prices next to each row stay per month.
  // The seats were shown on the plan page; the full amount appears at checkout.
  // Null while the book is unread or a row a turned-on extension needs is missing (THE FAILED READ, S-9): the total and the gold button wait.
  const extQuote = book === null ? null : orderQuote(book, draft, { ...loadOrgSetup(), planId, addons });
  const totalLabel = extQuote === null ? null : formatMxn(extQuote.extrasDue, language);
  const quoteMissing = book !== null && extQuote === null;

  useEffect(() => {
    if (!isStructureDraftReady(draft)) navigate('/onboarding', { replace: true });
  }, [draft, navigate]);

  const mark = (on: boolean) => (
    <span className={`org-check${on ? ' on' : ''}`}>{on ? <Check size={14} strokeWidth={3} /> : null}</span>
  );

  const money = (n: number) => `$${n.toLocaleString(language === 'es' ? 'es-MX' : 'en-US')}`;

  const toggle = (key: 'phone' | 'voice' | 'voicePro' | 'waSeller' | 'waTeam' | 'waCompany', qtyKey: keyof OrgAddons, cap: number) => {
    if (addons[key]) persist({ ...addons, [key]: false, [qtyKey]: 0 });
    else persist({ ...addons, [key]: true, [qtyKey]: cap });
  };

  const setQty = (key: 'phone' | 'voice' | 'voicePro' | 'waSeller' | 'waTeam' | 'waCompany', qtyKey: keyof OrgAddons, n: number) => {
    if (n <= 0) persist({ ...addons, [key]: false, [qtyKey]: 0 });
    else persist({ ...addons, [key]: true, [qtyKey]: n });
  };

  // [PRICING] The row's unit per month from the book (S-9): the plan's own row (voice is dearer on Light), null until the book is read
  // or when the row is missing - an empty slot then, never a typed figure. The struck form is the free row's (S-7).
  const unit = (id: ExtensionId): number | null => (book === null ? null : extensionUnit(book, id, planId, 'monthly'));
  const price = (id: ExtensionId) => {
    const n = unit(id);
    return n === null ? null : <span className="org-ext-price">{money(n)}</span>;
  };
  const struck = (id: ExtensionId) => {
    const n = unit(id);
    return n === null ? null : <span className="org-ext-price"><s>{money(n)}</s></span>;
  };

  // [PRICING] Included Zoom: one scope at a time (person / team / company). Tapping the active one turns it off.
  // Picking one turns "bring your own Zoom" off. The qty starts at that scope's maximum.
  const pickVideo = (scope: VideoScope) => {
    const on = addons.videoIncluded && addons.videoScope === scope;
    persist({
      ...addons,
      videoIncluded: !on,
      videoOwn: false,
      videoScope: scope,
      videoProvider: 'zoom',
      videoQty: on ? 0 : videoCap(scope),
    });
  };

  // [PRICING] Voice choice is a radio: basic (voice) or advanced (voicePro), only after the phone line is on. Advanced is locked on Light.
  const pickVoice = (which: 'basic' | 'advanced') => {
    if (!addons.phone) return;
    if (which === 'advanced' && lightPlan) return;
    const on = which === 'basic' ? addons.voice : addons.voicePro;
    const qty = addons.phoneQty || sellers;
    persist({
      ...addons,
      voice: !on && which === 'basic',
      voiceQty: !on && which === 'basic' ? qty : 0,
      voicePro: !on && which === 'advanced',
      voiceProQty: !on && which === 'advanced' ? qty : 0,
    });
  };

  const body = (
      <div className="org-ext">
        <div className="min-w-0 space-y-4">
          {/* [PRICING] THE FAILED READ (S-9): the one line above the cards when the book failed or a row an extension needs is missing; it is the retry. */}
          {failed || quoteMissing ? (
            <button type="button" className="ob-tag underline" onClick={retry}>
              {PRICES_FAILED_LINE}
            </button>
          ) : null}
          <section className="org-glass-card">
            <div className="org-card-title">
              <Phone size={18} />
              <h2>{t('settings.createOrganization.phoneTitle')}</h2>
            </div>
            <ToggleRow on={addons.phone} label={t('settings.createOrganization.phoneItem')} onToggle={() => {
              if (addons.phone) persist({ ...addons, phone: false, phoneQty: 0, voice: false, voiceQty: 0, voicePro: false, voiceProQty: 0 });
              else persist({ ...addons, phone: true, phoneQty: sellers });
            }}>
              <span className="org-row-end">
                {addons.phone && (
                  <Qty value={addons.phoneQty} max={sellers} onChange={(n) => {
                    if (n <= 0) persist({ ...addons, phone: false, phoneQty: 0, voice: false, voiceQty: 0, voicePro: false, voiceProQty: 0 });
                    else persist({ ...addons, phone: true, phoneQty: n });
                  }} />
                )}
                {price('phone')}
              </span>
            </ToggleRow>
            <div className={`org-voice-branch${addons.phone ? ' is-on' : ''}`}>
              <ToggleRow look="radio" on={addons.voice} locked={!addons.phone} label={t('settings.createOrganization.voiceItem')} onToggle={() => pickVoice('basic')}>
                <span className="org-row-end">
                  {addons.voice && (
                    <Qty value={addons.voiceQty} max={addons.phoneQty} onChange={(n) => setQty('voice', 'voiceQty', n)} />
                  )}
                  {price('voice')}
                </span>
              </ToggleRow>
              <ToggleRow
                look="radio"
                on={addons.voicePro}
                locked={!addons.phone || lightPlan}
                onToggle={() => pickVoice('advanced')}
                label={<>
                  {t('settings.createOrganization.voicePro')}
                  {addons.phone && lightPlan ? <span className="org-muted">{t('settings.createOrganization.voiceProNote')}</span> : null}
                </>}
              >
                <span className="org-row-end">
                  {addons.voicePro && (
                    <Qty value={addons.voiceProQty} max={addons.phoneQty} onChange={(n) => setQty('voicePro', 'voiceProQty', n)} />
                  )}
                  {price('voicePro')}
                </span>
              </ToggleRow>
            </div>
          </section>

          <section className="org-glass-card">
            <div className="org-card-title">
              <MessageCircle size={18} />
              <h2>{t('settings.createOrganization.channelsTitle')}</h2>
            </div>
            <div className="org-row">
              {mark(true)}
              <span className="org-row-label">{t('settings.createOrganization.whatsappIncluded')}</span>
              <span className="org-row-end">
                {/* [PRICING] The struck-through figure is the book's per-user WhatsApp API unit (S-7): display only (included WhatsApp is free), empty until the book is read. */}
                <span className="org-free">{t('settings.createOrganization.free')}</span>
                {struck('waPersonal')}
              </span>
            </div>
            <ToggleRow on={addons.waSeller} label={t('settings.createOrganization.waSeller')} onToggle={() => toggle('waSeller', 'waSellerQty', sellers)}>
              <span className="org-row-end">
                {addons.waSeller && (
                  <Qty value={addons.waSellerQty} max={sellers} onChange={(n) => setQty('waSeller', 'waSellerQty', n)} />
                )}
                {price('waPersonal')}
              </span>
            </ToggleRow>
            {/* [PRICING] The per-team WhatsApp API row is not sold on this screen (WU-145 P-4); the server's row stays. */}
            <ToggleRow on={addons.waCompany} label={t('settings.createOrganization.waCompany')} onToggle={() => toggle('waCompany', 'waCompanyQty', companies)}>
              {/* One per organization (ORG_EXTENSION_MAX), so there is no counter: tapping the row turns it on or off. */}
              <span className="org-row-end">
                {price('waCompany')}
              </span>
            </ToggleRow>
          </section>

          <section className="org-glass-card">
            <div className="org-card-title">
              <Video size={18} />
              <h2>{t('settings.createOrganization.videoTitle')}</h2>
            </div>
            <ToggleRow on={addons.videoIncluded && addons.videoScope === 'person'} label={t('settings.createOrganization.zoomByPerson')} onToggle={() => pickVideo('person')}>
              <span className="org-row-end">
                {addons.videoIncluded && addons.videoScope === 'person' && (
                  <Qty value={addons.videoQty} max={sellers} onChange={(n) => {
                    if (n <= 0) persist({ ...addons, videoIncluded: false, videoQty: 0 });
                    else persist({ ...addons, videoIncluded: true, videoScope: 'person', videoQty: n });
                  }} />
                )}
                {price('zoom')}
              </span>
            </ToggleRow>
            <ToggleRow on={addons.videoIncluded && addons.videoScope === 'team'} label={t('settings.createOrganization.zoomByTeam')} onToggle={() => pickVideo('team')}>
              <span className="org-row-end">
                {addons.videoIncluded && addons.videoScope === 'team' && (
                  <Qty value={addons.videoQty} max={teams} onChange={(n) => {
                    if (n <= 0) persist({ ...addons, videoIncluded: false, videoQty: 0 });
                    else persist({ ...addons, videoIncluded: true, videoScope: 'team', videoQty: n });
                  }} />
                )}
                {price('zoomTeam')}
              </span>
            </ToggleRow>
            <ToggleRow on={addons.videoIncluded && addons.videoScope === 'company'} label={t('settings.createOrganization.zoomByCompany')} onToggle={() => pickVideo('company')}>
              {/* One per organization, no counter (same as WhatsApp API por organización). */}
              <span className="org-row-end">
                {price('zoomCompany')}
              </span>
            </ToggleRow>
            <ToggleRow on={addons.videoOwn} label={t('settings.createOrganization.videoOwn')} onToggle={() => persist({ ...addons, videoOwn: !addons.videoOwn })}>
              <span className="org-row-end">{price('zoomOwn')}</span>
            </ToggleRow>
          </section>
        </div>
        {/* [SPACING] Side tree preview (OnboardingTreeView) sits in the right column of the .org-ext grid; see orgFlow.css. */}
        <aside className="org-tree-wrap">
          <OnboardingTreeView draft={draft} mode="sales" addons={addons} />
          <p className="mt-2 text-xs text-white/50">{t('settings.createOrganization.treeHint')}</p>
        </aside>
      </div>
  );

  const foot = (
      <div className="org-float">
        <button
          type="button"
          className="ob-back"
          onClick={() => navigate('/onboarding/plan')}
        >
          {t('settings.createOrganization.back')}
        </button>
        <div className="org-float-price">
          <b className="tabular-nums">{totalLabel}</b>
          <span>
            {extQuote === null
              ? null
              : extQuote.months > 1
                ? `${t('settings.createOrganization.extrasFor')} ${t(cycleTotalLabelKey(extQuote.cycle))}`
                : t('settings.createOrganization.extrasPerMonth')}
          </span>
        </div>
        <button
          type="button"
          className="btn-gold inline-flex items-center justify-center min-h-[48px] px-6 rounded-xl text-sm font-bold disabled:cursor-not-allowed disabled:opacity-60"
          disabled={extQuote === null}
          onClick={() => navigate('/onboarding/cuenta')}
        >
          {t('settings.createOrganization.continueAccount')}
        </button>
      </div>
  );

  const progress = publicJourney(draft.path, 'extensiones');
  return (
    <OnboardingFrame step={progress.step} total={progress.total} foot={foot}>
      <div className="ob-flow-copy">
        <h1 className="ob-flow-title">{t('settings.createOrganization.extrasTitle')}</h1>
        <p className="ob-flow-sub">
          {t('settings.createOrganization.extrasSubtitle')} {t('settings.createOrganization.extrasLater')}
        </p>
      </div>
      {body}
    </OnboardingFrame>
  );
};

export default CreateOrganizationExtensionsPage;
